import React, { useState } from 'react';
import type { Settings } from '../types/interfaces';
import { SUPPORTED_CURRENCIES, getCurrencySymbol, getCurrencyName } from '../utils/currency';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: Settings;
  onUpdateSettings: (updates: Partial<Settings>) => void;
}

declare global {
  interface Window {
    bsvPayments?: {
      requestPayment: (action: string, amount: number) => Promise<any>;
      getPaymentAmount: (action: string) => number;
    };
  }
}

export default function SettingsModal({ isOpen, onClose, settings, onUpdateSettings }: SettingsModalProps) {
  const [unlockingCurrency, setUnlockingCurrency] = useState(false);

  const handleCurrencyChange = async (newCurrency: string) => {
    // If currency is already unlocked, just change it
    if (settings.currencyUnlocked) {
      onUpdateSettings({ currency: newCurrency });
      return;
    }

    // If trying to change from USD, need to unlock
    if (newCurrency !== 'usd') {
      if (!window.bsvPayments || !window.bsvPayments.requestPayment) {
        alert('Connect MetaNet wallet');
        return;
      }

      setUnlockingCurrency(true);
      try {
        const amount = 50; // 50 satoshis to unlock currency feature
        const result = await window.bsvPayments.requestPayment('CURRENCY_UNLOCK', amount);
        
        if (result) {
          onUpdateSettings({ currency: newCurrency, currencyUnlocked: true });
        }
      } catch (error) {
        console.error('Currency unlock failed:', error);
      } finally {
        setUnlockingCurrency(false);
      }
    } else {
      // USD is free, just change it
      onUpdateSettings({ currency: newCurrency });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-2xl w-[90%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center"
        >
          &times;
        </button>
        <h2 className="text-3xl mb-8 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">⚙️ Settings</h2>
        <div className="flex flex-col gap-6">
          <div className="bg-gradient-to-br from-bg to-bg-secondary p-7 rounded-3xl border-2 border-primary/10 shadow-bubble">
            <h3 className="text-xl mb-5 text-text font-bold border-b-2 border-border pb-3 flex items-center gap-2">
              💱 Currency
            </h3>
            <div>
              <label htmlFor="currencySelect" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                Display Currency:
                {!settings.currencyUnlocked && settings.currency === 'usd' && (
                  <span className="ml-2 text-primary text-xs">(Free: USD only. Unlock others: 50 sats)</span>
                )}
                {settings.currencyUnlocked && (
                  <span className="ml-2 text-green-400 text-xs">✓ Unlocked</span>
                )}
              </label>
              <select
                id="currencySelect"
                value={settings.currency}
                onChange={(e) => handleCurrencyChange(e.target.value)}
                disabled={unlockingCurrency}
                className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble cursor-pointer hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {SUPPORTED_CURRENCIES.map((curr) => (
                  <option key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.name} ({curr.code.toUpperCase()})
                  </option>
                ))}
              </select>
              {unlockingCurrency && (
                <div className="mt-3 text-center text-text-secondary text-sm">
                  Processing payment...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

